import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '@/components/ui/badge';
import type {
  PipelineChatChartSpec,
  PipelineChatProposal,
} from '@/types/ai-pipeline';

const PIE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

function toTitleCase(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function buildSampleData(spec: PipelineChatChartSpec): Array<Record<string, string | number>> {
  if (spec.chartType === 'trend') {
    return [
      { [spec.xField]: 'Jan', [spec.yField]: 12 },
      { [spec.xField]: 'Feb', [spec.yField]: 16 },
      { [spec.xField]: 'Mar', [spec.yField]: 14 },
      { [spec.xField]: 'Apr', [spec.yField]: 18 },
    ];
  }

  if (spec.chartType === 'comparison') {
    return [
      { [spec.xField]: 'Tarakan', [spec.yField]: 12 },
      { [spec.xField]: 'Nunukan', [spec.yField]: 9 },
      { [spec.xField]: 'Tanjung Selor', [spec.yField]: 14 },
    ];
  }

  return [
    { [spec.xField]: 'Pangan', [spec.yField]: 34 },
    { [spec.xField]: 'Transportasi', [spec.yField]: 22 },
    { [spec.xField]: 'Energi', [spec.yField]: 18 },
    { [spec.xField]: 'Lainnya', [spec.yField]: 26 },
  ];
}

function ChartPreview({ spec }: { spec: PipelineChatChartSpec }) {
  const data = buildSampleData(spec);

  if (spec.chartType === 'trend') {
    return (
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={spec.xField} />
          <YAxis />
          <Tooltip />
          <Line
            type="monotone"
            dataKey={spec.yField}
            stroke="#2563eb"
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (spec.chartType === 'comparison') {
    return (
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={spec.xField} />
          <YAxis />
          <Tooltip />
          <Bar dataKey={spec.yField} fill="#10b981" />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={180}>
      <PieChart>
        <Tooltip />
        <Legend />
        <Pie data={data} dataKey={spec.yField} nameKey={spec.xField} outerRadius={60}>
          {data.map((_, index) => (
            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

interface InsightPanelProps {
  proposal: PipelineChatProposal | null;
}

export function InsightPanel({ proposal }: InsightPanelProps) {
  if (!proposal) {
    return (
      <section className="rounded-lg border bg-card p-3">
        <h3 className="text-sm font-semibold">Insight Package</h3>
        <p className="mt-2 text-xs text-muted-foreground">
          Insight panel akan muncul setelah proposal AI dibuat.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Insight Package</h3>
        <Badge variant="outline">Snapshot basis v{proposal.basePipelineVersion}</Badge>
      </div>

      <div className="space-y-3">
        {proposal.chartSpecs.map((spec, index) => (
          <article key={`${spec.chartType}-${index}`} className="rounded-md border p-2">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {toTitleCase(spec.chartType)}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {spec.xField} vs {spec.yField}
              </p>
            </div>
            <ChartPreview spec={spec} />
          </article>
        ))}
      </div>

      <article className="space-y-2 rounded-md border bg-muted/20 p-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide">Insights</h4>
        <ul className="space-y-1 text-sm">
          {proposal.insights.map((insight, index) => (
            <li key={`${insight}-${index}`}>• {insight}</li>
          ))}
        </ul>
      </article>

      <article className="space-y-2 rounded-md border bg-muted/20 p-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide">Data Caveats</h4>
        <ul className="space-y-1 text-sm">
          {(proposal.dataCaveats?.length ? proposal.dataCaveats : ['No caveats detected.']).map(
            (item, index) => (
              <li key={`${item}-${index}`}>• {item}</li>
            ),
          )}
        </ul>
      </article>

      <article className="space-y-2 rounded-md border bg-muted/20 p-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide">Recommended Actions</h4>
        <ul className="space-y-1 text-sm">
          {(
            proposal.recommendedActions?.length
              ? proposal.recommendedActions
              : ['Kirim prompt lanjutan untuk memperkaya pipeline.']
          ).map((item, index) => (
            <li key={`${item}-${index}`}>• {item}</li>
          ))}
        </ul>
      </article>
    </section>
  );
}
